import React, { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Typography,
  LinearProgress,
  Chip,
} from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import api from '../utils/api';
import UpgradeButton from './UpgradeButton';

interface SubscriptionStatus {
  user_type: string;
  subscription_status?: string;
  trial_expires_at?: string;
  stripe_customer_id?: string;
  stripe_subscription_id?: string;
  is_trial_expired: boolean;
  meetings_count: number;
  trial_meetings_limit: number;
}

interface TrialWarningProps {
  variant?: 'banner' | 'card';
  showUpgradeButton?: boolean;
}

const TrialWarning: React.FC<TrialWarningProps> = ({
  variant = 'banner',
  showUpgradeButton = true,
}) => {
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isAuthenticated) {
      loadSubscriptionStatus();
    }
  }, [isAuthenticated]);

  const loadSubscriptionStatus = async () => {
    try {
      const response = await api.get('/subscription/status');
      setSubscriptionStatus(response.data);
    } catch (err) {
      console.error('Error loading subscription status:', err);
    } finally {
      setLoading(false);
    }
  };

  const getTrialDaysLeft = () => {
    if (!subscriptionStatus?.trial_expires_at) return null;
    const expirationDate = new Date(subscriptionStatus.trial_expires_at);
    const now = new Date();
    const diffTime = expirationDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const getMeetingProgress = () => {
    if (!subscriptionStatus) return 0;
    return (subscriptionStatus.meetings_count / subscriptionStatus.trial_meetings_limit) * 100;
  };

  // Don't show warning if user is not on trial
  if (!subscriptionStatus || subscriptionStatus.user_type !== 'trial' || loading) {
    return null;
  }

  const daysLeft = getTrialDaysLeft();
  const meetingProgress = getMeetingProgress();
  const isExpired = subscriptionStatus.is_trial_expired;
  const isNearLimit = subscriptionStatus.meetings_count >= subscriptionStatus.trial_meetings_limit - 1;

  const severity = isExpired ? 'error' : isNearLimit ? 'warning' : 'info';

  const getWarningMessage = () => {
    if (isExpired) {
      return 'Your trial has expired. Please upgrade to continue using the service.';
    }
    
    if (isNearLimit) {
      return `You've used ${subscriptionStatus.meetings_count}/${subscriptionStatus.trial_meetings_limit} trial meetings. Upgrade to continue.`;
    }

    return `Trial: ${subscriptionStatus.meetings_count}/${subscriptionStatus.trial_meetings_limit} meetings used.`;
  };

  if (variant === 'card') {
    return (
      <Box
        sx={{
          p: 3,
          borderRadius: 2,
          background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.1) 0%, rgba(255, 193, 7, 0.1) 100%)',
          border: '1px solid rgba(255, 152, 0, 0.3)',
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <WarningIcon color="warning" sx={{ mr: 1 }} />
          <Typography variant="h6" fontWeight={600}>
            Trial Account
          </Typography>
          {daysLeft !== null && (
            <Chip 
              label={`${daysLeft} days left`} 
              color="warning" 
              size="small" 
              sx={{ ml: 2 }}
            />
          )}
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {getWarningMessage()}
        </Typography>

        {/* Meeting Progress */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2">Meeting Usage</Typography>
            <Typography variant="body2">
              {subscriptionStatus.meetings_count}/{subscriptionStatus.trial_meetings_limit}
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={meetingProgress} 
            color={isNearLimit ? 'warning' : 'primary'}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>

        {showUpgradeButton && (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <UpgradeButton 
              variant="contained" 
              color="primary"
              plan="paid"
            >
              Upgrade Plan
            </UpgradeButton>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Alert 
      severity={severity}
      icon={<WarningIcon />}
      sx={{ mb: 3 }}
      action={
        showUpgradeButton ? (
          <UpgradeButton 
            variant="outlined" 
            size="small"
            color="inherit"
            plan="paid"
            showSnackbar={false}
          >
            Upgrade
          </UpgradeButton>
        ) : undefined
      }
    >
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          {isExpired ? 'Trial Expired' : isNearLimit ? 'Trial Limit Reached' : 'Trial Account'}
        </Typography>
        <Typography variant="body2">
          {getWarningMessage()}
        </Typography>
        
        {!isExpired && (
          <Box sx={{ mt: 2, maxWidth: 300 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="caption">Meeting Usage</Typography>
              <Typography variant="caption">
                {subscriptionStatus.meetings_count}/{subscriptionStatus.trial_meetings_limit}
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={meetingProgress} 
              color={isNearLimit ? 'warning' : 'primary'}
              sx={{ height: 6, borderRadius: 3 }}
            />
          </Box>
        )}
      </Box>
    </Alert>
  );
};

export default TrialWarning; 